const periodSuffix = ({periodIds}) => (periodIds.length === 1 ? '' : 's');

export const PER_PERIOD_MESSAGE_KEYS = Object.freeze({
   FLAGS: 'flags',
   NO_DATA: 'noData',
   SME_NEEDS_INPUT: 'smeNeedsInput',
   SME_PERIOD_LOOKS_GOOD: 'smePeriodLooksGood',
   SME_PERIOD_COMPLETE: 'smePeriodComplete',
   SME_DONE: 'smeDone',
   CUSTOMER_ASSIGN: 'customerAssign',
   CUSTOMER_NEEDS_INPUT: 'customerNeedsInput',
   CUSTOMER_PERIOD_LOOKS_GOOD: 'customerPeriodLooksGood',
   CUSTOMER_WAITING_ON_OTHERS: 'customerWaitingOnOthers',
   CUSTOMER_PERIOD_COMPLETE: 'customerPeriodComplete',
   CUSTOMER_READY_TO_MARK_COMPLETE: 'customerReadyToMarkComplete',
   CUSTOMER_SECTION_COMPLETE: 'customerSectionComplete',
});

export const CROSS_PERIOD_MESSAGE_KEYS = Object.freeze({
   FLAGS: 'flags',
   NO_DECLARATION: 'noDeclaration',
   SECTION_SKIPPED: 'sectionSkipped',
   CUSTOMER_NO_VENDORS: 'customerNoVendors',
   CUSTOMER_ASSIGN_VENDORS: 'customerAssignVendors',
   CUSTOMER_NEEDS_INPUT: 'customerNeedsInput',
   CUSTOMER_WAITING_ON_OTHERS: 'customerWaitingOnOthers',
   CUSTOMER_READY_TO_MARK_COMPLETE: 'customerReadyToMarkComplete',
   CUSTOMER_SECTION_COMPLETE: 'customerSectionComplete',
   SME_NEEDS_INPUT: 'smeNeedsInput',
   SME_WAITING_ON_OTHERS: 'smeWaitingOnOthers',
   SME_DONE: 'smeDone',
});

export const ASSIGNMENT_TYPES = Object.freeze({
   supplies: 'suppliesAssignments',
   cloud: 'cloudcomputingAssignments',
   contractors: 'contractorAssignments',
   employees: 'employeeAssignments',
});

export const resolveAssignmentType = (expenseType) => {
   return ASSIGNMENT_TYPES[expenseType] || `${expenseType}Assignments`;
};

export const EXPENSE_ENTITY_LABEL_CONFIG = Object.freeze({
   supplies: {
      singular: 'Vendor',
      plural: 'Vendors',
      singularLower: 'vendor',
      pluralLower: 'vendors',
   },
   cloud: {
      singular: 'Vendor',
      plural: 'Vendors',
      singularLower: 'vendor',
      pluralLower: 'vendors',
   },
   contractors: {
      singular: 'Contractor',
      plural: 'Contractors',
      singularLower: 'contractor',
      pluralLower: 'contractors',
   },
   employees: {
      singular: 'Employee',
      plural: 'Employees',
      singularLower: 'employee',
      pluralLower: 'employees',
   },
});

export const PER_PERIOD_MESSAGE_CONFIG = {
   supplies: {
      flags: {
         header: () => "Some of this data doesn't look quite right.",
         description: () =>
            "We've flagged some items that we think might not be correct based on our experience. Please have a look at the flags raised below. Modify the values if our correct, verify the ones that you're certain are correct, and correct any that are marked as requiring change. As always, please let me know if you have any questions.",
      },

      noData: {
         header: () => 'No qualified supplies expenses yet.',
         description: (ctx) =>
            `The accounts provided so far do not contain expenses in the time period${periodSuffix(
               ctx
            )}. You can go back and continue to upload expense accounts, or update your answer on the first page of this section to indicate that your company made no qualified supplies purchases during this time period.`,
      },

      smeNeedsInput: {
         header: () => 'How much of this spending went to R&D Supplies?',
         description: () =>
            'For each vendor, please enter the total percentage of the spending that went toward R&D Supplies. Then, indicate what percentage of the qualified purchases went to each project.',
      },

      smePeriodLooksGood: {
         header: () => 'This time period looks good.',
         description: () =>
            "All of your supplies have valid data for this time period, please check other time periods. If you're confident all the information for this time period is accurate, click <strong>'Mark Assignment Complete'</strong> and proceed to other time period to continue with this section.",
      },

      smePeriodComplete: {
         header: () => 'This time period is complete.',
         description: () =>
            'All of your supplies vendors have valid data for this time period, please check other time periods.',
      },

      smeDone: {
         header: () => "You're done.",
         description: () =>
            "This section has been completed, so as long as everything still looks correct, you're all done here! To make any changes to this section, you'll need to contact administrator to unlock it.",
      },

      customerAssign: {
         header: () => 'Do you want to assign some of this work to someone else?',
         description: () =>
            'You can assign them all to yourself if you like, or you can invite other people from your company and assign vendors to those users with the knowledge of their R&D contributions.',
      },

      customerNeedsInput: {
         header: () => 'How much of this spending went to R&D Supplies?',
         description: () =>
            'For each vendor, please enter the total percentage of the spending that went toward R&D Supplies. Then, indicate what percentage of the qualified purchases went to each project.',
      },

      customerPeriodLooksGood: {
         header: () => 'This time period looks good.',
         description: () =>
            "All of your supplies have valid data for this time period, please check other time periods. If you're confident all the information for this time period is accurate, click <strong>'Mark Assignment Complete'</strong> and proceed to other time period to continue with this section.",
      },

      customerWaitingOnOthers: {
         header: () => "You're done, but we're waiting on others.",
         description: () =>
            "You're all done here, but we're waiting on other users to enter their supplies vendors' time. Visit your dashboard to see what's next.",
      },

      customerPeriodComplete: {
         header: () => 'This time period is complete.',
         description: () =>
            'All of your supplies vendors have valid data for this time period, please check other time periods.',
      },

      customerReadyToMarkComplete: {
         header: () => 'This section is ready to mark complete.',
         description: () =>
            "All of your supplies' vendors have valid data for all time periods. If you're confident all the information is accurate, click <strong>'Mark Section Complete'</strong>.",
      },

      customerSectionComplete: {
         header: () => 'This section is complete.',
         description: () =>
            "As long as everything still looks correct, you're all done here! If you need to make any additional changes, you'll need to unlock this section first.",
      },
   },

   cloud: {
      flags: {
         header: () => "Some of this data doesn't look quite right.",
         description: () =>
            "We've flagged some items that we think might not be correct based on our experience. Please have a look at the flags raised below. Modify the values if our correct, verify the ones that you're certain are correct, and correct any that are marked as requiring change. As always, please let me know if you have any questions.",
      },

      noData: {
         header: () => 'No qualified cloud computing expenses yet.',
         description: (ctx) =>
            `The accounts provided so far do not contain expenses in the time period${periodSuffix(
               ctx
            )}. You can go back and continue to upload expense accounts, or update your answer on the first page of this section to indicate that your company made no qualified cloud computing purchases during this time period.`,
      },

      smeNeedsInput: {
         header: () => 'How much of this spending went to Cloud Computing?',
         description: () =>
            'For each vendor, please enter the total percentage of the spending that went toward Cloud Computing. Then, indicate what percentage of the qualified purchases went to each project.',
      },

      smePeriodLooksGood: {
         header: () => 'This time period looks good.',
         description: () =>
            "All of your cloud computing have valid data for this time period, please check other time periods. If you're confident all the information for this time period is accurate, click <strong>'Mark Assignment Complete'</strong> and proceed to other time period to continue with this section.",
      },

      smePeriodComplete: {
         header: () => 'This time period is complete.',
         description: () =>
            'All of your cloud computing vendors have valid data for this time period, please check other time periods.',
      },

      smeDone: {
         header: () => "You're done.",
         description: () =>
            "This section has been completed, so as long as everything still looks correct, you're all done here! To make any changes to this section, you'll need to contact administrator to unlock it.",
      },

      customerAssign: {
         header: () => 'Do you want to assign some of this work to someone else?',
         description: () =>
            'You can assign them all to yourself if you like, or you can invite other people from your company and assign vendors to those users with the knowledge of their R&D contributions.',
      },

      customerNeedsInput: {
         header: () => 'How much of this spending went to Cloud Computing?',
         description: () =>
            'For each vendor, please enter the total percentage of the spending that went toward Cloud Computing. Then, indicate what percentage of the qualified purchases went to each project.',
      },

      customerPeriodLooksGood: {
         header: () => 'This time period looks good.',
         description: () =>
            "All of your cloud computing data have valid data for this time period, please check other time periods. If you're confident all the information for this time period is accurate, click <strong>'Mark Assignment Complete'</strong> and proceed to other time period to continue with this section.",
      },

      customerWaitingOnOthers: {
         header: () => "You're done, but we're waiting on others.",
         description: () =>
            "You're all done here, but we're waiting on other users to enter their cloud computing data vendors' time. Visit your dashboard to see what's next.",
      },

      customerPeriodComplete: {
         header: () => 'This time period is complete.',
         description: () =>
            'All of your cloud computing vendors have valid data for this time period, please check other time periods.',
      },

      customerReadyToMarkComplete: {
         header: () => 'This section is ready to mark complete.',
         description: () =>
            "All of your cloud computing' vendors have valid data for all time periods. If you're confident all the information is accurate, click <strong>'Mark Section Complete'</strong>.",
      },

      customerSectionComplete: {
         header: () => 'This section is complete.',
         description: () =>
            "As long as everything still looks correct, you're all done here! If you need to make any additional changes, you'll need to unlock this section first.",
      },
   },

   employees: {
      flags: {
         header: () => "Some of this data doesn't look quite right.",
         description: () =>
            "We've flagged some items that we think might not be correct based on our experience. Please have a look at the flags raised below. Modify the values if our correct, verify the ones that you're certain are correct, and correct any that are marked as requiring change. As always, please let me know if you have any questions.",
      },

      noData: {
         header: () => 'No qualified employee expenses yet.',
         description: (ctx) =>
            `The records provided so far do not contain qualified employee expenses in the time period${periodSuffix(
               ctx
            )}. You can go back and continue to upload or review employee records, or update your answer on the first page of this section to indicate that your company made no qualified employee expenditures during this time period.`,
      },

      smeNeedsInput: {
         header: () =>
            'Tell us about the R&D time contributed by the employees listed below that are assigned to you.',
         description: () =>
            'Enter how each employee divided their R&D time among your projects for this time period.',
      },

      smePeriodLooksGood: {
         header: () => 'This time period looks good.',
         description: () =>
            "All of your employee assignments have valid data for this time period, please check other time periods. If you're confident all the information for this time period is accurate, click <strong>'Mark Assignment Complete'</strong> and proceed to other time period to continue with this section.",
      },

      smePeriodComplete: {
         header: () => 'This time period is complete.',
         description: () =>
            'All of your assigned employees have valid data for this time period, please check other time periods.',
      },

      smeDone: {
         header: () => "You're done.",
         description: () =>
            "This section has been completed, so as long as everything still looks correct, you're all done here! To make any changes to this section, you'll need to contact administrator to unlock it.",
      },

      customerAssign: {
         header: () => 'Do you want to assign some of this work to someone else?',
         description: () =>
            'You can assign them all to yourself if you like, or you can invite other people from your company and assign employees to those users with the knowledge of their R&D contributions.',
      },

      customerNeedsInput: {
         header: () =>
            'Tell us about the R&D time contributed by the employees listed below that are assigned to you.',
         description: () =>
            'Enter how each employee divided their R&D time among your projects for this time period.',
      },

      customerPeriodLooksGood: {
         header: () => 'This time period looks good.',
         description: () =>
            "All of your employee assignments have valid data for this time period, please check other time periods. If you're confident all the information for this time period is accurate, click <strong>'Mark Assignment Complete'</strong> and proceed to other time period to continue with this section.",
      },

      customerWaitingOnOthers: {
         header: () => "You're done, but we're waiting on others.",
         description: () =>
            "You're all done here, but we're waiting on other users to enter their assigned employees' time. Visit your dashboard to see what's next.",
      },

      customerPeriodComplete: {
         header: () => 'This time period is complete.',
         description: () =>
            'All of your assigned employees have valid data for this time period, please check other time periods.',
      },

      customerReadyToMarkComplete: {
         header: () => 'This section is ready to mark complete.',
         description: () =>
            "All of your assigned employees have valid data for all time periods. If you're confident all the information is accurate, click <strong>'Mark Section Complete'</strong>.",
      },

      customerSectionComplete: {
         header: () => 'This section is complete.',
         description: () =>
            "As long as everything still looks correct, you're all done here! If you need to make any additional changes, you'll need to unlock this section first.",
      },
   },

   contractors: {
      flags: {
         header: () => "Some of this data doesn't look quite right.",
         description: () =>
            "We've flagged some items that we think might not be correct based on our experience. Please have a look at the flags raised below. Modify the values if our correct, verify the ones that you're certain are correct, and correct any that are marked as requiring change. As always, please let me know if you have any questions.",
      },

      noData: {
         header: () => 'No qualified contractor expenses yet.',
         description: (ctx) =>
            `The records provided so far do not contain qualified contractor expenses in the time period${periodSuffix(
               ctx
            )}. You can go back and continue to upload or review contractor records, or update your answer on the first page of this section to indicate that your company made no qualified contractor expenditures during this time period.`,
      },

      smeNeedsInput: {
         header: () => 'How much contractor time went to R&D?',
         description: () =>
            'This is where you tell us how the time of those contracted by your company to do R&D was divided among your R&D projects in each time period.',
      },

      smePeriodLooksGood: {
         header: () => 'This time period looks good.',
         description: () =>
            "All of your contractor assignments have valid data for this time period, please check other time periods. If you're confident all the information for this time period is accurate, click <strong>'Mark Assignment Complete'</strong> and proceed to other time period to continue with this section.",
      },

      smePeriodComplete: {
         header: () => 'This time period is complete.',
         description: () =>
            'All of your assigned contractors have valid data for this time period, please check other time periods.',
      },

      smeDone: {
         header: () => "You're done.",
         description: () =>
            "This section has been completed, so as long as everything still looks correct, you're all done here! To make any changes to this section, you'll need to contact administrator to unlock it.",
      },

      customerAssign: {
         header: () => 'Do you want to assign some of this work to someone else?',
         description: () =>
            'You can assign them all to yourself if you like, or you can invite other people from your company and assign contractors to those users with the knowledge of their R&D contributions.',
      },

      customerNeedsInput: {
         header: () => 'How much contractor time went to R&D?',
         description: () =>
            'This is where you tell us how the time of those contracted by your company to do R&D was divided among your R&D projects in each time period.',
      },

      customerPeriodLooksGood: {
         header: () => 'This time period looks good.',
         description: () =>
            "All of your contractor assignments have valid data for this time period, please check other time periods. If you're confident all the information for this time period is accurate, click <strong>'Mark Assignment Complete'</strong> and proceed to other time period to continue with this section.",
      },

      customerWaitingOnOthers: {
         header: () => "You're done, but we're waiting on others.",
         description: () =>
            "You're all done here, but we're waiting on other users to enter their assigned contractors' time. Visit your dashboard to see what's next.",
      },

      customerPeriodComplete: {
         header: () => 'This time period is complete.',
         description: () =>
            'All of your assigned contractors have valid data for this time period, please check other time periods.',
      },

      customerReadyToMarkComplete: {
         header: () => 'This section is ready to mark complete.',
         description: () =>
            "All of your assigned contractors have valid data for all time periods. If you're confident all the information is accurate, click <strong>'Mark Section Complete'</strong>.",
      },

      customerSectionComplete: {
         header: () => 'This section is complete.',
         description: () =>
            "As long as everything still looks correct, you're all done here! If you need to make any additional changes, you'll need to unlock this section first.",
      },
   },
};

export const CROSS_PERIOD_MESSAGE_CONFIG = {
   supplies: {
      flags: {
         header: () => 'Here you can tell us how much you spent on R&D supplies per year.',
         description: () =>
            'Supplies purchased for and consumed by R&D activities qualify for the R&D tax credit. Choose a year to tell us how much of your supplies purchases were qualified purchases. Questions? Contact me with the secure messaging feature in the top right.',
      },

      noDeclaration: {
         header: () => 'Did any of these projects use qualified supplies?',
         description: () =>
            'Supply costs that qualify for credit are any supplies that were used exclusively for your R&D experimentation.',
      },

      sectionSkipped: {
         header: () => 'This section is complete.',
         description: () =>
            "Since your company didn't consume any qualified supplies for R&D, we can just skip this section. Visit the dashboard to see what other info you can provide.",
      },

      customerNoVendors: {
         header: () => 'Tell us about the supplies vendors you worked with.',
         description: () => 'Please add supplies vendors using the buttons above to continue.',
      },

      customerAssignVendors: {
         header: () => 'Do you want to assign some of this work to someone else?',
         description: () =>
            'You can assign them all to yourself if you like, or you can invite other people from your company and assign vendors to those users with the knowledge of their R&D contributions.',
      },

      customerNeedsInput: {
         header: () => 'Here you can tell us how much you spent on R&D supplies per year.',
         description: () =>
            'Supplies purchased for and consumed by R&D activities qualify for the R&D tax credit. Choose a year to tell us how much of your supplies purchases were qualified purchases. Questions? Contact me with the secure messaging feature in the top right.',
      },

      customerWaitingOnOthers: {
         header: () => "You're done, but we're waiting on others.",
         description: () =>
            "You're all done here, but we're waiting on other users to enter their supplies data. Visit your dashboard to see what's next.",
      },

      customerReadyToMarkComplete: {
         header: () => 'This section is ready to mark complete.',
         description: () =>
            "All of your supplies' vendors have valid data for all time periods. If you're confident all the information is accurate, click <strong>'Mark Section Complete'</strong>.",
      },

      customerSectionComplete: {
         header: () => 'This section is complete.',
         description: () =>
            "This section has been completed, so as long as everything still looks correct, you're all done here! To make any changes to this section, you'll need to unlock it first.",
      },

      smeNeedsInput: {
         header: () => 'Here you can tell us how much you spent on R&D supplies per year.',
         description: () =>
            'Supplies purchased for and consumed by R&D activities qualify for the R&D tax credit. Choose a year to tell us how much of your supplies purchases were qualified purchases. Questions? Contact me with the secure messaging feature in the top right.',
      },

      smeWaitingOnOthers: {
         header: () => 'This section is complete.',
         description: () =>
            "You're all done here, but we're waiting on other users to enter their supplies' time. Visit your dashboard to see what's next.",
      },

      smeDone: {
         header: () => "You're done.",
         description: () =>
            "This section has been completed, so as long as everything still looks correct, you're all done here! To make any changes to this section, you'll need to contact administrator to unlock it.",
      },
   },

   cloud: {
      flags: {
         header: () => 'Here you can tell us how much you spent on R&D cloud computing per year.',
         description: () =>
            'Cloud computing purchased for and consumed by R&D activities qualify for the R&D tax credit. Choose a year to tell us how much of your cloud computing purchases were qualified purchases. Questions? Contact me with the secure messaging feature in the top right.',
      },

      noDeclaration: {
         header: () => 'Did any of these projects use qualified cloud computing?',
         description: () =>
            'Cloud Computing costs that qualify for credit are any supplies that were used exclusively for your R&D experimentation.',
      },

      sectionSkipped: {
         header: () => 'This section is complete.',
         description: () =>
            "Since your company didn't consume any qualified cloud computing for R&D, we can just skip this section. Visit the dashboard to see what other info you can provide.",
      },

      customerNoVendors: {
         header: () => 'Tell us about the cloud computing vendors you worked with.',
         description: () =>
            'Please add cloud computing vendors using the buttons above to continue.',
      },

      customerAssignVendors: {
         header: () => 'Do you want to assign some of this work to someone else?',
         description: () =>
            'You can assign them all to yourself if you like, or you can invite other people from your company and assign vendors to those users with the knowledge of their R&D contributions.',
      },

      customerNeedsInput: {
         header: () => 'Here you can tell us how much you spent on R&D cloud computing per year.',
         description: () =>
            'Cloud computing purchased for and consumed by R&D activities qualify for the R&D tax credit. Choose a year to tell us how much of your cloud computing purchases were qualified purchases. Questions? Contact me with the secure messaging feature in the top right.',
      },

      customerWaitingOnOthers: {
         header: () => "You're done, but we're waiting on others.",
         description: () =>
            "You're all done here, but we're waiting on other users to enter their cloud computing data. Visit your dashboard to see what's next.",
      },

      customerReadyToMarkComplete: {
         header: () => 'This section is ready to mark complete.',
         description: () =>
            "All of your cloud computing' vendors have valid data for all time periods. If you're confident all the information is accurate, click <strong>'Mark Section Complete'</strong>.",
      },

      customerSectionComplete: {
         header: () => 'This section is complete.',
         description: () =>
            "This section has been completed, so as long as everything still looks correct, you're all done here! To make any changes to this section, you'll need to unlock it first.",
      },

      smeNeedsInput: {
         header: () => 'Here you can tell us how much you spent on R&D cloud computing per year.',
         description: () =>
            'Cloud computing purchased for and consumed by R&D activities qualify for the R&D tax credit. Choose a year to tell us how much of your cloud computing purchases were qualified purchases. Questions? Contact me with the secure messaging feature in the top right.',
      },

      smeWaitingOnOthers: {
         header: () => 'This section is complete.',
         description: () =>
            "You're all done here, but we're waiting on other users to enter their cloud computing' time. Visit your dashboard to see what's next.",
      },

      smeDone: {
         header: () => "You're done.",
         description: () =>
            "This section has been completed, so as long as everything still looks correct, you're all done here! To make any changes to this section, you'll need to contact administrator to unlock it.",
      },
   },

   employees: {
      flags: {
         header: () => 'Here you can tell us how much time your employees spent on R&D per year.',
         description: () =>
            'Choose a year to tell us how each employee divided their R&D time among your projects. Questions? Contact me with the secure messaging feature in the top right.',
      },

      noDeclaration: {
         header: () => 'Did any of these projects use qualified employees?',
         description: () =>
            'Employee wages that qualify for credit are wages paid to employees for the time they spent performing, supervising, or supporting your R&D activities.',
      },

      sectionSkipped: {
         header: () => 'This section is complete.',
         description: () =>
            "Since your company didn't incur any qualified employee expenses for R&D, we can just skip this section. Visit the dashboard to see what other info you can provide.",
      },

      customerNoVendors: {
         header: () => 'Tell us about the employees who worked on your R&D projects.',
         description: () => 'Please add employees using the buttons above to continue.',
      },

      customerAssignVendors: {
         header: () => 'Do you want to assign some of this work to someone else?',
         description: () =>
            'You can assign them all to yourself if you like, or you can invite other people from your company and assign employees to those users with the knowledge of their R&D contributions.',
      },

      customerNeedsInput: {
         header: () => 'Here you can tell us how much time your employees spent on R&D per year.',
         description: () =>
            'Choose a year to tell us how each employee divided their R&D time among your projects. Questions? Contact me with the secure messaging feature in the top right.',
      },

      customerWaitingOnOthers: {
         header: () => "You're done, but we're waiting on others.",
         description: () =>
            "You're all done here, but we're waiting on other users to enter their employees' time. Visit your dashboard to see what's next.",
      },

      customerReadyToMarkComplete: {
         header: () => 'This section is ready to mark complete.',
         description: () =>
            "All of your assigned employees have valid data for all time periods. If you're confident all the information is accurate, click <strong>'Mark Section Complete'</strong>.",
      },

      customerSectionComplete: {
         header: () => 'This section is complete.',
         description: () =>
            "This section has been completed, so as long as everything still looks correct, you're all done here! To make any changes to this section, you'll need to unlock it first.",
      },

      smeNeedsInput: {
         header: () => 'Here you can tell us how much time your employees spent on R&D per year.',
         description: () =>
            'Choose a year to tell us how each employee divided their R&D time among your projects. Questions? Contact me with the secure messaging feature in the top right.',
      },

      smeWaitingOnOthers: {
         header: () => 'This section is complete.',
         description: () =>
            "You're all done here, but we're waiting on other users to enter their assigned employees' time. Visit your dashboard to see what's next.",
      },

      smeDone: {
         header: () => "You're done.",
         description: () =>
            "This section has been completed, so as long as everything still looks correct, you're all done here! To make any changes to this section, you'll need to contact administrator to unlock it.",
      },
   },

   contractors: {
      flags: {
         header: () => 'Here you can tell us how much contractor time went to R&D per year.',
         description: () =>
            'Choose a year to tell us how the time of those contracted by your company to do R&D was divided among your R&D projects. Questions? Contact me with the secure messaging feature in the top right.',
      },

      noDeclaration: {
         header: () => 'Did any of these projects use qualified contractors?',
         description: () =>
            'Payments made to contractors for research & development qualify for the R&D tax incentive when the financial risk and intellectual rights are retained by your company.',
      },

      sectionSkipped: {
         header: () => 'This section is complete.',
         description: () =>
            "Since your company didn't incur any qualified contractor expenses for R&D, we can just skip this section. Visit the dashboard to see what other info you can provide.",
      },

      customerNoVendors: {
         header: () => 'Tell us about the contractors who worked on your R&D projects.',
         description: () => 'Please add contractors using the buttons above to continue.',
      },

      customerAssignVendors: {
         header: () => 'Do you want to assign some of this work to someone else?',
         description: () =>
            'You can assign them all to yourself if you like, or you can invite other people from your company and assign contractors to those users with the knowledge of their R&D contributions.',
      },

      customerNeedsInput: {
         header: () => 'Here you can tell us how much contractor time went to R&D per year.',
         description: () =>
            'Choose a year to tell us how the time of those contracted by your company to do R&D was divided among your R&D projects. Questions? Contact me with the secure messaging feature in the top right.',
      },

      customerWaitingOnOthers: {
         header: () => "You're done, but we're waiting on others.",
         description: () =>
            "You're all done here, but we're waiting on other users to enter their contractors' time. Visit your dashboard to see what's next.",
      },

      customerReadyToMarkComplete: {
         header: () => 'This section is ready to mark complete.',
         description: () =>
            "All of your assigned contractors have valid data for all time periods. If you're confident all the information is accurate, click <strong>'Mark Section Complete'</strong>.",
      },

      customerSectionComplete: {
         header: () => 'This section is complete.',
         description: () =>
            "This section has been completed, so as long as everything still looks correct, you're all done here! To make any changes to this section, you'll need to unlock it first.",
      },

      smeNeedsInput: {
         header: () => 'Here you can tell us how much contractor time went to R&D per year.',
         description: () =>
            'Choose a year to tell us how the time of those contracted by your company to do R&D was divided among your R&D projects. Questions? Contact me with the secure messaging feature in the top right.',
      },

      smeWaitingOnOthers: {
         header: () => 'This section is complete.',
         description: () =>
            "You're all done here, but we're waiting on other users to enter their assigned contractors' time. Visit your dashboard to see what's next.",
      },

      smeDone: {
         header: () => "You're done.",
         description: () =>
            "This section has been completed, so as long as everything still looks correct, you're all done here! To make any changes to this section, you'll need to contact administrator to unlock it.",
      },
   },
};

export const TITLE_CONFIG = {
   supplies: 'Supplies',
   cloud: 'Cloud Computing',
   employees: 'Employee Time',
   contractors: 'Contract Research',
};

export const INTRO_CONFIG = {
   supplies: `The costs of supplies consumed during research and development qualify for the R&D credit too, as long as they were consumed exclusively as a part of your R&D projects. In this section, you will export the accounting records that document supplies purchases, and the percent of spending with each vendor that {QUALIFIES_LINK} as true R&D supply expenses.`,
   cloud: `The costs of cloud computing consumed during research and development qualify for the R&D credit too, as long as they were consumed exclusively as a part of your R&D projects. In this section, you will export the accounting records that document cloud computing purchases, and the percent of spending with each vendor that {QUALIFIES_LINK} as true R&D cloud computing expenses.`,
   contractors: `Contractor wages spent on research and development are a key source of R&D tax credits. In this section, you will tell us about the contract-based workers who contributed to R&D for your company and share their wage information. This is often information that no single person at your company will know completely, so in this section it is easy for you to invite others at your company to collaborate with you on the data entry. Let's get to it!`,
   employees: `Wages paid for time spent on R&D are where most companies get the most tax credit. In this section, you will assign employees to Onboard users at your company who can tell us how much time those employees spent on R&D in years past. This will be quick and painless, so let's dive in!`,
};

export const ACTIVE_PERIOD_INTRO_CONFIG = {
   supplies: {
      title: 'What percent of your supplies purchases were consumed for R&D?',
      description:
         'Click <b>Enter time</b> below to tell us how much of your spending per-vendor was on qualified R&D supplies during that time period.',
   },

   cloud: {
      title: 'What percent of your cloud computing purchases were consumed for R&D?',
      description:
         'Click <b>Enter time</b> below to tell us how much of your spending per-vendor was on qualified R&D cloud computing during that time period.',
   },

   employees: {
      title: 'How much time have your employees spent on R&D?',
      description:
         'Click <b>Enter time</b> below to tell us about employee time spent on qualified R&D work during that time period.',
   },

   contractors: {
      title: 'How much time did your contractors spend on R&D?',
      description:
         "Click <b>Enter time</b> on a time period below to tell us about contractors' time in that time period.",
   },
};

export const ADD_VENDOR_IMPORT_CONFIG = {
   supplies: {
      intro: ({prettyPeriodNames}) =>
         `Supplies expenses can be imported from the expense accounts in your company's bookkeeping software. Export any accounts which may have been used to purchase supplies for R&D from ${prettyPeriodNames} and import them here. You can download a template file containing the importable fields.`,
      templateButtonLabel: 'Download Expense Account Template',
      requiredColumnsIntro:
         "Imported files don't have to use this exact format. They can contain any number of additional columns as long as they include:",
      requiredColumns: ({hasManyLocations}) =>
         `Vendor Name,${
            hasManyLocations ? ' Location, ' : ' '
         }Transaction Date, Transaction Amount (Debit)`,
   },

   cloud: {
      intro: ({prettyPeriodNames}) =>
         `Cloud computing expenses can be imported from the expense accounts in your company's bookkeeping software. Export any accounts which may have been used to purchase cloud computing for R&D from ${prettyPeriodNames} and import them here. You can download a template file containing the importable fields.`,
      templateButtonLabel: 'Download Expense Account Template',
      requiredColumnsIntro:
         "Imported files don't have to use this exact format. They can contain any number of additional columns as long as they include:",
      requiredColumns: ({hasManyLocations}) =>
         `Vendor Name,${
            hasManyLocations ? ' Location, ' : ' '
         }Transaction Date, Transaction Amount (Debit)`,
   },

   contractors: {
      intro: ({prettyPeriodNames}) =>
         `Contractor expenses can be imported from the expense accounts in your company's bookkeeping software. Export any accounts which may have been used to purchase contractor services for R&D from ${prettyPeriodNames} and import them here. You can download a template file containing the importable fields.`,
      templateButtonLabel: 'Download Expense Account Template',
      requiredColumnsIntro:
         "Imported files don't have to use this exact format. They can contain any number of additional columns as long as they include:",
      requiredColumns: ({hasManyLocations}) =>
         `Vendor Name,${
            hasManyLocations ? ' Location, ' : ' '
         }Transaction Date, Transaction Amount (Debit)`,
   },

   employees: {
      intro: ({prettyPeriodNames}) =>
         `Employees can be imported from your company's payroll register. Export a roster covering ${prettyPeriodNames} and import it here. You can download a template file containing the importable fields.`,
      templateButtonLabel: 'Download Employee Account Template',
      requiredColumnsIntro:
         "Imported files don't have to use this exact format. They can contain any number of additional columns as long as they include:",
      requiredColumns: () => 'Full Name, Location (US State), Federal Taxable Wages, Paycheck Date',
   },
};

/**
 * Upload categories for expense import data files.
 * TODO: Confirm contractor category wiring before enabling its import flow in AddVendorModal.
 */
export const EXPENSE_DATA_UPLOAD_CATEGORY_CONFIG = Object.freeze({
   supplies: {
      name: 'Supplies Data',
      category: 'SUPPLIES',
   },
   cloud: {
      name: 'Cloud Computing Data',
      category: 'CLOUDCOMPUTING',
   },
   contractors: {
      name: null,
      category: null,
   },
   employees: {
      name: 'Employees CSV',
      category: 'EMPLOYEES',
   },
});

/**
 * Upload categories for expense supporting documents.
 * TODO: Add/confirm employee supporting-docs upload category when backend support is added.
 */
export const EXPENSE_SUPPORTING_DOCS_UPLOAD_CATEGORY_CONFIG = Object.freeze({
   supplies: {
      name: 'Supplies Supporting Docs',
      category: 'SUPPLIES_DOCS',
   },
   cloud: {
      name: 'Cloud Computing Docs',
      category: 'CLOUDCOMPUTING_DOCS',
   },
   contractors: {
      name: 'Contractor Contracts',
      category: 'CONTRACTOR_DOCS',
   },
   employees: {
      name: null,
      category: null,
   },
});
